import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useAddOnboarding, useProjectAccess, useRevokeAccess, useStudioMe, useStudioTeam } from '@/api/studio';
import type { Project } from '@/api/types';
import { ConfirmButton } from '@/components/ConfirmDialog';
import { Status } from '@/components/ui';
import { SECRET_NOTICE } from '@/lib/access';
import { errorMessage } from '@/lib/errors';
import { formatWhen } from '@/lib/format';
import { secretStatus } from '@/lib/status';
import { OpenOnce } from './OpenOnce';
import { hasOpenedValue } from './openedValues';

/** The project's «Доступы»: ask the client for credentials outside the launch, and open what came back. */
export function StudioProjectAccess({ p }: { p: Project }) {
  const { data } = useProjectAccess(p.id);
  const { data: team } = useStudioTeam();
  const me = useStudioMe();
  const add = useAddOnboarding();
  const revoke = useRevokeAccess();
  const [title, setTitle] = useState('');
  const [hint, setHint] = useState('');
  const [toId, setToId] = useState('');
  const recipient = toId || me?.id || '';
  const secretOf = (id?: string) => data?.secrets.find((s) => s.id === id);

  return (
    <section className="max-w-3xl">
      <p className="mb-4 text-sm text-muted">Доступы, которые нужны не только на старте. Клиент получит уведомление и увидит запрос в «Сейчас» и в «Доступах» с кнопкой «Передать».</p>
      <form
        className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_170px_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          add.mutate(
            { projectId: p.id, item: { kind: 'access', scope: 'access', title: title.trim(), hint: hint.trim(), ...(recipient ? { toId: recipient } : {}) } },
            { onSuccess: () => (setTitle(''), setHint('')) },
          );
        }}
      >
        <input className="field" placeholder="Что нужно, например «Авито»" aria-label="Что нужно" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="field" placeholder="Подсказка клиенту" aria-label="Подсказка клиенту" value={hint} onChange={(e) => setHint(e.target.value)} />
        <select className="field" aria-label="Кому передать" value={recipient} onChange={(e) => setToId(e.target.value)}>
          {team?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.id === me?.id ? `${t.name} (я)` : t.name}
            </option>
          ))}
        </select>
        <button className="btn-primary" disabled={!title.trim() || add.isPending}>
          <KeyRound className="size-4" /> Запросить доступ
        </button>
      </form>
      {add.error && (
        <p role="alert" className="mt-2 text-sm text-overdue">
          {errorMessage(add.error)}
        </p>
      )}
      <h2 className="mb-2 mt-8 text-sm font-semibold">Запросы</h2>
      {data?.requests.length ? (
        <ul className="divide-y divide-line border-y border-line">
          {data.requests.map((r) => {
            const s = secretOf(r.secretId);
            return (
              <li key={r.id} className="py-2.5">
                <div className="flex items-center gap-3">
                  <span className="flex-1 text-sm">
                    {r.title}
                    {r.scope === 'launch' && <span className="ml-2 text-xs text-muted">из запуска</span>}
                    {r.hint && <span className="block text-xs text-muted">{r.hint}</span>}
                  </span>
                  <span className="text-xs text-muted">{r.status === 'done' ? `передан ${formatWhen(r.doneAt ?? '')}` : 'ждём клиента'}</span>
                </div>
                {s && (
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted">
                    <span>
                      {s.from.name} → {s.to.name}, {formatWhen(s.createdAt)}
                    </span>
                    <Status tone={secretStatus[s.status].tone}>{secretStatus[s.status].label}</Status>
                    {s.status === 'waiting' && (
                      <ConfirmButton label="Отозвать" className="btn-ghost h-7 px-2 text-xs" title="Отозвать доступ?" confirmLabel="Отозвать" quiet onConfirm={() => revoke.mutate(s.id)}>
  Доступ «{s.title}» перестанет открываться. Если получатель его уже открыл, значения у себя нужно сменить.
</ConfirmButton>
                    )}
                    {((s.status === 'waiting' && s.mine) || hasOpenedValue(s.id)) && <OpenOnce id={s.id} title={s.title} from={s.from.name} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted">Запросов пока нет.</p>
      )}
      {(() => {
        const asked = new Set(data?.requests.map((r) => r.secretId));
        const loose = data?.secrets.filter((s) => !asked.has(s.id)) ?? [];
        if (!loose.length) return null;
        return (
          <>
            <h2 className="mb-2 mt-8 text-sm font-semibold">Переданные без запроса</h2>
            <ul className="divide-y divide-line border-y border-line">
              {loose.map((s) => (
                <li key={s.id} className="py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="flex-1 text-sm">
                      {s.title}
                      <span className="block text-xs text-muted">
                        {s.from.name} → {s.to.name}, {formatWhen(s.createdAt)}
                      </span>
                    </span>
                    <Status tone={secretStatus[s.status].tone}>{secretStatus[s.status].label}</Status>
                    {s.status === 'waiting' && (
                      <ConfirmButton label="Отозвать" className="btn-ghost h-7 px-2 text-xs" title="Отозвать доступ?" confirmLabel="Отозвать" quiet onConfirm={() => revoke.mutate(s.id)}>
  Доступ «{s.title}» перестанет открываться. Если получатель его уже открыл, значения у себя нужно сменить.
</ConfirmButton>
                    )}
                  </div>
                  {((s.status === 'waiting' && s.mine) || hasOpenedValue(s.id)) && <OpenOnce id={s.id} title={s.title} from={s.from.name} />}
                </li>
              ))}
            </ul>
          </>
        );
      })()}
      <p className="mt-2 text-xs text-muted">{SECRET_NOTICE}</p>
    </section>
  );
}
